import fs from 'fs/promises'
import path from 'path'
import chalk from 'chalk'
import inquirer from 'inquirer'

interface InitOptions {
  framework: string
  output: string
  yes: boolean
}

const frameworkTemplates = {
  vite: {
    client: {
      'VITE_API_URL': 'z.string().url()',
      'VITE_ENV': "z.enum(['development', 'production', 'test'])",
    },
    server: {
      'DATABASE_URL': 'z.string().url()',
      'SECRET_KEY': 'z.string().min(16)',
    },
  },
  nextjs: {
    client: {
      'NEXT_PUBLIC_API_URL': 'z.string().url()',
      'NEXT_PUBLIC_ENV': "z.enum(['development', 'production', 'test'])",
    },
    server: {
      'DATABASE_URL': 'z.string().url()',
      'SECRET_KEY': 'z.string().min(16)',
    },
  },
  cra: {
    client: {
      'REACT_APP_API_URL': 'z.string().url()',
      'REACT_APP_ENV': "z.enum(['development', 'production', 'test'])",
    },
    server: {},
  },
  express: {
    client: {},
    server: {
      'DATABASE_URL': 'z.string().url()',
      'PORT': 'z.string().transform((val: string) => parseInt(val, 10)).default("3000")',
      'SECRET_KEY': 'z.string().min(16)',
      'NODE_ENV': "z.enum(['development', 'production', 'test'])",
    },
  },
}

export async function init(options: InitOptions) {
  const { framework, output, yes } = options

  console.log(chalk.blue('🚀 Initializing envfortress configuration...\n'))

  // Validate framework
  if (!frameworkTemplates[framework as keyof typeof frameworkTemplates]) {
    throw new Error(`Invalid framework: ${framework}. Supported frameworks: ${Object.keys(frameworkTemplates).join(', ')}`)
  }

  // Check if output file already exists
  try {
    await fs.access(output)
    if (!yes) {
      const { overwrite } = await inquirer.prompt([
        {
          type: 'confirm',
          name: 'overwrite',
          message: `File ${output} already exists. Overwrite it?`,
          default: false,
        },
      ])
      if (!overwrite) {
        console.log(chalk.yellow('Operation cancelled.'))
        return
      }
    }
  } catch {
    // File doesn't exist, that's fine
  }

  // Generate the env file content
  const template = frameworkTemplates[framework as keyof typeof frameworkTemplates]
  const content = generateEnvFile(template, framework)

  // Ensure directory exists
  const outputDir = path.dirname(output)
  await fs.mkdir(outputDir, { recursive: true })

  // Write the file
  await fs.writeFile(output, content, 'utf-8')

  console.log(chalk.green(`✅ Created ${output}`))
  console.log(chalk.blue(`\n📝 Next steps:`))
  console.log(chalk.gray(`1. Review and customize the generated schema in ${output}`))
  console.log(chalk.gray(`2. Create a .env file with your actual values`))
  console.log(chalk.gray(`3. Run 'envfortress validate' to check your setup`))
  console.log(chalk.gray(`4. Run 'envfortress sync' to generate .env.example`))
}

function generateEnvFile(template: { client: Record<string, string>; server: Record<string, string> }, framework: string): string {
  const clientVars = Object.entries(template.client)
    .map(([key, validation]) => `    ${key}: ${validation},`)
    .join('\n')

  const serverVars = Object.entries(template.server)
    .map(([key, validation]) => `    ${key}: ${validation},`)
    .join('\n')

  return `import { createEnv } from 'envfortress'
import { z } from 'zod'

export const env = createEnv({
  client: {
${clientVars}
  },
  server: {
${serverVars}
  },
})

export default env
`
} 